import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Code, Shield, Users, Mic, Heart, Target, Eye, Zap, HeartHandshake, BookOpen, TrendingUp, RefreshCw, Users2 } from 'lucide-react';
import { DottedPattern, GradientBlob } from '../components/DecorativeElements';
import ImageWithFallback from '../components/ImageWithFallback';
import './About.css';

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const strengths = [
    {
      title: 'Award Winning',
      description: 'Recognized for excellence in IT solutions and innovation.',
      icon: <Award size={32} />,
    },
    {
      title: 'Professional Staff',
      description: 'A team of dedicated professionals, committed to your success.',
      icon: <Users size={32} />,
    },
    {
      title: '24/7 Support',
      description: 'Always here to help, no matter the time or place.',
      icon: <Shield size={32} />,
    },
    {
      title: 'Fair Prices',
      description: 'Offering premium services at competitive rates.',
      icon: <Heart size={32} />,
    },
  ];

  const values = [
    {
      title: 'Client-Centric Approach',
      description: 'We prioritize our clients\' needs and work closely with them to achieve their objectives.',
      icon: <Users2 size={32} />,
    },
    {
      title: 'Innovation',
      description: 'We embrace change and continuously seek new ways to deliver exceptional value.',
      icon: <Zap size={32} />,
    },
    {
      title: 'Integrity',
      description: 'We conduct our business with the highest level of honesty and transparency.',
      icon: <Shield size={32} />,
    },
    {
      title: 'Collaboration',
      description: 'We believe in the power of teamwork, both within our organization and with our clients.',
      icon: <HeartHandshake size={32} />,
    },
    {
      title: 'Continuous Learning',
      description: 'We are committed to staying ahead of industry trends and continuously improving our skills and knowledge.',
      icon: <BookOpen size={32} />,
    },
    {
      title: 'Results-Oriented',
      description: 'We focus on delivering tangible results that drive business success.',
      icon: <TrendingUp size={32} />,
    },
    {
      title: 'Adaptability',
      description: 'We are flexible and responsive, ready to meet the evolving needs of our clients.',
      icon: <RefreshCw size={32} />,
    },
    {
      title: 'Community Engagement',
      description: 'We are dedicated to giving back to the communities we serve and making a positive impact.',
      icon: <Heart size={32} />,
    },
  ];

  return (
    <div className="about-page">
      <section className="about-hero">
        <DottedPattern />
        <GradientBlob className="blob-hero" />
        <div className="container">
          <motion.h1
            className="page-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            About Us
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Discover who we are and what drives us at Team FullStack
          </motion.p>
        </div>
      </section>

      <section className="about-content" ref={ref}>
        <div className="container">
          {/* Who We Are Section */}
          <motion.div
            className="about-intro"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2>Who We Are</h2>
            <p>
              Team FullStack is a forward-thinking IT solutions provider with <strong>4+ years of experience</strong> committed to driving digital transformation across industries. With two years of dedicated service, we have established ourselves as a trusted partner for businesses aiming to excel in the digital landscape.
            </p>
            <p>
              We specialize in offering comprehensive IT services, from cutting-edge digital strategies to robust cybersecurity solutions, ensuring that our clients stay ahead in the ever-evolving tech world.
            </p>
          </motion.div>

          {/* Strengths Section */}
          <motion.div
            className="strengths-section"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="section-title">Why Choose Us</h2>
            <div className="strengths-grid">
              {strengths.map((strength, index) => (
                <motion.div
                  key={index}
                  className="strength-card"
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                >
                  <div className="strength-icon-wrapper">{strength.icon}</div>
                  <h3>{strength.title}</h3>
                  <p>{strength.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Mission Section */}
          <motion.div
            className="mission-vision-section"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="mission-card">
              <div className="mission-icon">
                <Target size={48} />
              </div>
              <h2>Our Mission</h2>
              <p className="mission-description">
                Empowering businesses with digital solutions tailored to their unique needs.
              </p>
              <p>
                At Team FullStack, our mission is to empower businesses with cutting-edge digital solutions that drive growth, efficiency, and innovation. We believe in creating tailored strategies that meet the specific needs of each client, ensuring that they achieve their goals in the most effective way possible.
              </p>
            </div>

            <div className="vision-card">
              <div className="vision-icon">
                <Eye size={48} />
              </div>
              <h2>Our Vision</h2>
              <p className="vision-description">
                Leading in digital excellence and client success on a global scale.
              </p>
              <p>
                We envision a future where Team FullStack is synonymous with digital excellence. Our goal is to be at the forefront of technological advancements, providing our clients with the tools they need to thrive in a rapidly evolving digital world. We are committed to making a global impact, one client at a time.
              </p>
            </div>
          </motion.div>

          {/* Values Section */}
          <motion.div
            className="values-section"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <h2 className="values-title">Our Values</h2>
            <p className="values-intro">
              The principles that guide our work and define our culture.
            </p>
            <div className="values-grid">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  className="value-card"
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                >
                  <div className="value-icon-wrapper">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
